import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {concat, EMPTY, switchMap, tap, toArray} from 'rxjs';
import {KatalogeService} from '../../../../services/kataloge.service';
import {Katalog} from '../../../../shared/model/katalog.model';
import {CatalogAssignment} from '../../../../shared/model/catalog-assignment.model';
import {IBerater} from '../../../../shared/model/berater.model';
import {FormsModule} from '@angular/forms';
import {BeraterService} from '../../../../services/berater.service';
import {MessageService} from '../../../../shared/components/message.service';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {TranslatePipe} from '@ngx-translate/core';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent} from '@angular/material/card';
import {UserRoleService} from '../../../../services/userRole.service';
import {NgClass} from '@angular/common';

@Component({
  selector: 'app-roles-ba',
  imports: [
    FormsModule,
    MatButton,
    MatCheckbox,
    TranslatePipe,
    MatIcon,
    MatInput,
    MatLabel,
    MatFormField,
    MatIconButton,
    MatCardContent,
    MatCard,
    NgClass
  ],
  templateUrl: './roles-ba.component.html',
  styleUrl: './roles-ba.component.scss',
  standalone: true
})
export class RolesBaComponent implements OnInit, OnChanges {
  @Input() berater: IBerater;
  /**
   * NP20-889: Die zugewiesenen Kataloge kommen aufgeloest vom Parent. Der Berater traegt seit
   * dem Wegfall der eingebetteten Kopien nur noch catalogId und Bewertungen; Name, Preisgruppe
   * und RVSE-Version stehen ausschliesslich im Master-Katalog.
   */
  @Input() assignedCatalogs: Katalog[] = [];
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  /**
   * NP20-911: Der vom Backend gelieferte Stand geht nach oben, damit der Parent ihn uebernimmt
   * und seine Zaehler neu berechnet — dasselbe Muster wie in SkillsBaComponent und den uebrigen
   * Abschnitten. NP20-889: Das PATCH liefert die Zuweisungen, nicht mehr den ganzen Berater.
   */
  @Output() saved = new EventEmitter<CatalogAssignment[]>();
  /**
   * Bittet den Parent, den Berater neu zu laden (Review PR #702).
   *
   * Nur fuer den Teil-Fehlschlag beim Speichern: DELETE und PATCH sind nicht atomar, und
   * nachdem ein DELETE durch ist, kennt das Frontend den Serverstand nicht mehr. Dann ist ein
   * Neuladen der einzige Weg zu einer verlaesslichen Anzeige.
   */
  @Output() reloadRequired = new EventEmitter<void>();
  @Output() exportSelectionChange = new EventEmitter<string[]>();

  selectedExportIds: string[] = [];
  allowedRoles = ['Office', 'Sale', 'Admin'];
  editMode = false;
  allKataloge: Katalog[] = [];
  filtersCatalogues: Katalog[] = [];
  searchTerm = '';

  /**
   * NP20-889: Die Bearbeitung laeuft nur noch auf den ausgewaehlten Katalog-Ids. Beim Speichern
   * wird gegen originalSelectedIds verglichen, weil PATCH und DELETE getrennte Operationen sind.
   */
  selectedIds: string[] = [];
  private originalSelectedIds: string[] = [];
  /**
   * NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Durchlauf
   * aus — zwei DELETE-Folgen gleichzeitig wuerden sich gegenseitig verlieren (siehe save()).
   */
  isSaving = false;

  constructor(
    private katalogService: KatalogeService,
    private beraterService: BeraterService,
    private userService: UserRoleService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    if (this.allowedRoles.some((role) => this.userService.hasRole(role))) {
      this.katalogService.query().subscribe((result) => {
        this.allKataloge = result.body ?? [];
        this.filtersCatalogues = [...this.allKataloge];
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedExportIds = [];
        this.exportSelectionChange.emit([]);
      }
    } else if (changes['assignedCatalogs'] && this.exportMode && !this.twoPagerMode) {
      this.autoSelectAll();
    }
  }

  private autoSelectAll(): void {
    this.selectedExportIds = this.assignedCatalogs.map(k => k.id);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  toggleExportSelection(id: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExportIds.includes(id)) this.selectedExportIds.push(id);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== id);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  onKatalogToggle(katalogId: string, checked: boolean) {
    if (checked) {
      if (!this.selectedIds.includes(katalogId)) {
        this.selectedIds = [...this.selectedIds, katalogId];
      }
    } else {
      this.selectedIds = this.selectedIds.filter((id) => id !== katalogId);
    }
  }

  isKatalogSelected(katalog: Katalog): boolean {
    return this.selectedIds.includes(katalog.id);
  }

  /**
   * NP20-889: Abgewaehlte Kataloge brauchen ein DELETE — das PATCH ist ein Upsert und laesst
   * nicht mitgeschickte Zuweisungen bewusst stehen. Neu gewaehlte gehen mit leerem ratings-Array
   * ins PATCH; die Bewertungen setzt der Berater danach im Kenntnisse-Abschnitt.
   */
  save() {
    if (this.isSaving) return;
    this.isSaving = true;
    const removed = this.originalSelectedIds.filter((id) => !this.selectedIds.includes(id));
    const added = this.selectedIds
      .filter((id) => !this.originalSelectedIds.includes(id))
      .map((catalogId) => ({catalogId, ratings: []}));

    /**
     * Zaehlt die tatsaechlich durchgelaufenen DELETEs.
     *
     * DELETE und PATCH sind nicht atomar (Review PR #702): scheitert das zweite DELETE, ist
     * das erste schon geschrieben, das PATCH laeuft gar nicht mehr, und `originalSelectedIds`
     * beschreibt den Serverstand nicht mehr. Nur dieser Zaehler unterscheidet den Fall vom
     * harmlosen "es wurde noch nichts geschrieben".
     */
    let removalsDone = 0;

    // Die DELETEs laufen bewusst nacheinander: das Backend liest die Zuweisungsliste, filtert
    // und schreibt sie komplett zurueck — parallele Aufrufe wuerden sich gegenseitig verlieren.
    const removals$ = removed.length
      ? concat(...removed.map((catalogId) =>
        this.beraterService.deleteCatalogAssignment(this.berater.id, catalogId)
          .pipe(tap(() => removalsDone++))))
      : EMPTY;

    // Auch ohne Neuzugaenge wird gepatcht: die Antwort ist der vollstaendige Stand nach dem
    // Schreiben und damit die einzige Quelle, die der Parent uebernehmen muss.
    removals$
      .pipe(
        toArray(),
        switchMap(() => this.beraterService.upsertCatalogAssignments(this.berater.id, added))
      )
      .subscribe({
        next: (assignments) => {
          this.isSaving = false;
          // NP20-915: editMode erst nach der Antwort schliessen, sonst sieht ein Fehlschlag wie Erfolg aus.
          this.editMode = false;
          this.saved.emit(assignments);
        },
        error: () => {
          this.isSaving = false;
          if (removalsDone > 0) {
            // Serverseitig ist schon etwas weg, in der Liste steht noch der alte Stand. Den
            // Nutzer auf seiner falschen Anzeige weiterarbeiten zu lassen, waere schlimmer als
            // seine Auswahl zu verwerfen: Bearbeitung schliessen und den echten Stand holen.
            this.editMode = false;
            this.messageService.error('baCatalog.roleBa.partialErrorMessage');
            this.reloadRequired.emit();
            return;
          }
          // Nichts geschrieben: Bearbeitungsmodus bleibt offen, die Auswahl steht weiter in
          // der Liste und laesst sich erneut speichern.
          this.messageService.error('baCatalog.roleBa.errorMessage');
        },
      });
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      this.originalSelectedIds = this.assignedCatalogs.map((k) => k.id);
      this.selectedIds = [...this.originalSelectedIds];
      this.editMode = true;
    }
  }

  onCancelEdit() {
    this.selectedIds = [...this.originalSelectedIds];
    this.editMode = false;
  }

  hasChanges(): boolean {
    return (
      this.selectedIds.length !== this.originalSelectedIds.length ||
      this.selectedIds.some((id) => !this.originalSelectedIds.includes(id))
    );
  }

  filterCatalogues() {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term) {
      this.filtersCatalogues = [...this.allKataloge];
      return;
    }

    const terms = term.split(/\s+/);

    this.filtersCatalogues = this.allKataloge.filter((k) => {
      const visibleText = `
        ${(k.rollenbezeichnung || '').toLowerCase()}
        (rvse${k.rvseVersion ?? ''})
        pg${k.preisgruppe ?? ''}
        ${(k.bezeichnung || '').toLowerCase()}
      `.replace(/\s+/g, ' ');

      return terms.every((t) => visibleText.includes(t));
    });
  }

  clearSearch() {
    this.searchTerm = '';
    this.filtersCatalogues = [...this.allKataloge];
  }

  isAllegro(catalog: Katalog): boolean {
    return catalog.rollenbezeichnung
      ?.toString()
      .toLowerCase()
      .includes('allegro');
  }
}
